/** Every failure the API layer raises: non-2xx, `success:false`, or a network failure (status 0). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export const isApiError = (e: unknown, code?: string): e is ApiError =>
  e instanceof ApiError && (code === undefined || e.code === code);

type ErrorBody = { success?: boolean; error?: { code?: string; message?: string } };

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

/** Builds an ApiError from a parsed (or text) error body; falls back to the HTTP status. */
export function toApiError(status: number, body: unknown): ApiError {
  const err = isRecord(body) ? (body as ErrorBody).error : undefined;
  if (err?.code) return new ApiError(status, err.code, err.message ?? err.code);
  if (status >= 500) return new ApiError(status, "UPSTREAM_UNAVAILABLE", "Service unavailable, retry in a moment");
  return new ApiError(status, `HTTP_${status}`, typeof body === "string" && body ? body : `Request failed (${status})`);
}

/**
 * Unwraps the `{success,data}` envelope (spec §2.1). Throws ApiError on `success:false`
 * or a non-2xx status. 202/204 (no body) resolve to `undefined`.
 */
export function unwrapEnvelope(status: number, body: unknown): unknown {
  if (status < 200 || status >= 300) throw toApiError(status, body);
  if (body === undefined || body === null || body === "") return undefined;
  if (isRecord(body) && body["success"] === false) throw toApiError(status, body);
  return isRecord(body) && "data" in body ? body["data"] : body;
}

/**
 * Lists come back as an array or as a keyed object (`{partners:[…]}`, `{rows:[…]}`…), spec §2.4.
 * Returns the array at `key`, else the first array-valued property, else [].
 */
export function toList<T>(data: unknown, key?: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (!isRecord(data)) return [];
  if (key && Array.isArray(data[key])) return data[key] as T[];
  const first = Object.values(data).find(Array.isArray);
  return (first as T[] | undefined) ?? [];
}

/** User-facing message for an error caught by a page or form (spec §2.3). */
export function errorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return e instanceof Error ? e.message : "Something went wrong";
  switch (e.code) {
    case "UPSTREAM_UNAVAILABLE":
    case "UPSTREAM_NOT_CONFIGURED":
    case "NETWORK":
      return "Service unavailable, retry in a moment.";
    case "FORBIDDEN":
      return "You do not have permission to do this.";
    case "NOT_FOUND":
      return "Not found.";
    default:
      return e.status === 429 ? "Too many attempts, wait a moment." : e.message;
  }
}
